import { useEffect, useMemo, useRef, useState } from "react";
// Material UI
import { alpha, Card, CardContent, CardHeader, Grid, Tab, Theme, Typography } from "@mui/material";
// Redux
import { IDelayCategory, IDelayCode, IFlight, IFullFlight } from "@vmosolutions/oc-interfaces";
import { useSelector } from "react-redux";
import { IRootState } from "../redux/reducers/CombinedReducers";
// Forms
import { ArrivalMessages } from "./MovementMessages/ArrivalMessages";
import { DepartureMessages } from "./MovementMessages/DepartureMessages";
import { FlightMessages } from "./MovementMessages/FlightMessages";
// Components
import moment from "moment-timezone";
import { FlightChip } from "src/components/Flights/FlightChip";
import { DisplayLastModification } from "src/components/FormikComponents/DisplayLastModification";
import { getFlightById } from "src/redux/actions/flights/FlightData.Actions";
import { convertMilli2DaysHrsMin } from "src/utils/index";
import { OcTabs } from "../components/OcTabs/OcTabs";
import { IDelayOptions } from "./FlightEditor/DelaysForm/DelaysForm";
import { formatDelayCode, sortDelayOptions } from "src/utils/delayCode";

export interface IAircraftOptions {
  value: string;
  label: string;
}

interface IMovementMessage {
  id: string;
  path: string[];
  splitPercentage: number;
  windowProps: {
    selectedFlight: IFlight;
    // flightId: string;
    // aircraftId: string;
  };
}

const renderFormHeader = (
  title: string,
  etaOut: string,
  etaOff: string,
  etaOn: string,
  etaIn: string,
  flightDuration: string,
  flight: IFlight,
  subtitle?: string
): JSX.Element => {
  return (
    <>
      <div className="form_header">
        <CardHeader title={title} className="form-title" />
        {!!subtitle && <span className="form-subtitle">{subtitle}</span>}

        <Grid item style={{ marginTop: 5, marginLeft: -10 }}>
          <DisplayLastModification
            userId={flight?.modifiedBy ?? flight.createdBy}
            time={flight.modifiedAt ?? flight.createdAt}
          />
        </Grid>

        <div className="flight_data" style={{ width: "100%" }}>
          <Grid container wrap="nowrap">
            <Grid container direction="column" className="estimated_departure_times">
              <span className="header">Est. Departure Times</span>
              <Grid container>
                <Grid container direction="column" justifyContent="flex-end" style={{ width: 101 }}>
                  <span className="label">Out Time</span>
                  <span className="label">Off Time</span>
                </Grid>
                <Grid container direction="column" className="values" style={{ width: "auto" }}>
                  <span className="value">{etaOut}</span>
                  <span className="value">{etaOff}</span>
                </Grid>
              </Grid>
            </Grid>
            <Grid container direction="column" className="estimated_arrival_times">
              <span className="header">Est. Arrival Times</span>
              <Grid container>
                <Grid container direction="column" justifyContent="flex-end" style={{ width: 101 }}>
                  <span className="label">On Time</span>
                  <span className="label">In Time</span>
                </Grid>
                <Grid container direction="column" className="values" style={{ width: "auto" }}>
                  <span className="value">{etaOn}</span>
                  <span className="value">{etaIn}</span>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid container style={{ marginTop: 6 }}>
            <Grid container direction="column" justifyContent="flex-end" style={{ width: 101 }}>
              <span className="label">Block Time</span>
            </Grid>
            <Grid container direction="column" className="values" style={{ width: "auto" }}>
              <span className="value">{flightDuration}</span>
            </Grid>
          </Grid>
        </div>
      </div>
    </>
  );
};

const movementMessageStyles = (theme: Theme): any => ({
  MovementMessage_Container: {
    overflow: "auto",
    flexWrap: "nowrap",
    backgroundColor: theme.palette.background.paper,
  },

  MovementMessage_Menu: {
    position: "relative",
    left: "8px",
    "& .flightchip_group_container": {
      margin: "10px 0px 6px -4px",
      "#flightgroup": { margin: 0 },
    },
  },

  MovementMessage_Card: {
    flexGrow: 1,
    boxShadow: theme.shadows[5],
    margin: "10px 8px 0px 8px",
    minHeight: "calc(100% - 20px)",
    backgroundColor: theme.custom.timeline.contextListBackground,
    "&.vertical": {
      margin: "8px 8px 8px 0px",
    },
    "& .MuiCardContent-root": {
      padding: "0px 10px",
      "& .form_header": {
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        margin: "10px",
        "& .form-title": { padding: 0 },
        "& .form-subtitle": {
          color: theme.custom.forms.labels,
          margin: 0,
          fontSize: "14px",
          height: "1.2em",
          alignSelf: "center",
        },
        "& .flight_data": {
          margin: "10px",
          padding: "10px",
          border: `1px solid ${theme.custom.forms.inputBorder}`,
          display: "flex",
          flexWrap: "wrap",
          "& span.header": {
            fontSize: 12,
            color: theme.custom.forms.labels,
            textAlign: "center",
            marginBottom: "6px",
          },
          "& span.label": {
            fontWeight: 300,
            fontSize: 12,
            textAlign: "right",
            marginRight: "10px",
            color: theme.custom.forms.inputText,
          },
          "& span.value": {
            fontSize: 12,
            color: theme.palette.primary.contrastText,
          },
        },
      },
    },
    "& .no_events": { margin: "10px" },
    "& .departure_message_form, & .arrival_message_form": {
      "& .MuiGrid-item": {
        width: "363px",
      },
      "& p.required_fields_label": {
        margin: 0,
        fontWeight: 300,
        fontSize: 10,
        textTransform: "uppercase",
        color: theme.custom.forms.labels,
      },
      "& .required_fields": {
        backgroundColor: alpha(theme.custom.forms.labels, 0.2),
        margin: "2px 0px",
        "& .flight_message_input": {
          "& .input_control": {
            "& label": { width: "auto" },
            "& .input-base": { width: "100%" },
          },
        },
      },
      "& .buttons": {
        display: "flex",
        padding: "5px 5px 5px 95px",
        "& .form_buttons": {
          width: "auto",
          height: "30px",
          fontSize: 11,
          lineHeight: "12px",
          padding: "2px 8px",
          margin: "0 2px",
        },
      },
    },
  },

  overlay: {
    display: "none",
    top: 0,
    position: "absolute",
    height: "100%",
    width: "100%",
    backgroundColor: theme.custom.timeline.contextListBackground,
    opacity: 0,
    "&.active": {
      display: "block",
      opacity: 0.75,
    },
  },

  flightId: {
    width: "300px",
    margin: "0 !important",
    padding: "5px 15px !important",
    fontSize: 12,
    color: theme.custom.forms.labels,
    opacity: 0.25,
    "&:hover": {
      opacity: 0.75,
    },
  },
});

/**
 * Submit Movement Message window. Functionality is tied to selected aircraft.
 * @param props -
 */
export const MovementMessages = (props: IMovementMessage): JSX.Element => {
  const { path, windowProps } = props;
  const { selectedFlight } = windowProps;

  const containerRef = useRef<HTMLDivElement>(null);
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);
  const uiSettings = useSelector((state: IRootState) => state.UISettings);
  const { delayCodeFormat, delayCodeSortOrder } = uiSettings;

  const [refresh, setRefresh] = useState<boolean>(false);
  const [flight, setFlight] = useState<IFullFlight>(null);
  const flightId = selectedFlight?._id;

  // Pending change in case we'd ever have to quickly reload flight pased on selection made in timeline
  // const selectedEvents = useSelector((state: IRootState) => state.timelineFlights.selectedEvents);
  // const selectedFlights: IFlight[] = useMemo(() => {
  //  return selectedEvents.filter((event: IFlight | IGroundEvent) => isFlight(event)) as IFlight[];
  //}, [selectedEvents]);

  // Even though selected flight is passed through window props, retrieve the IFlight data from the db to ensure info is accurate.
  // This is done because when saving layouts, selected Flight would be saved in the window props and could become stale and no longer accurate.
  useEffect(() => {
    const getFlight = async (): Promise<void> => {
      const response = await getFlightById(flightId);
      if (response?._id) {
        setFlight(response);
      }
    };
    // If flightId is available, retrieve and load flight data
    if (flightId) getFlight();
  }, [flightId]);

  // If refresh is true and there's already flight data loaded, make a second call to refresh.
  useEffect(() => {
    const getFlight = async (): Promise<void> => {
      const response = await getFlightById(flight?._id);
      if (response?._id) {
        setFlight(response);
        setRefresh(false); // Reset status on refresh.
      }
    };
    // If flightId is available, retrieve and load flight data
    if (flight && refresh) getFlight();
  }, [flight, refresh]);

  // Delay Codes
  const delayCategories = useSelector((state: IRootState) => state.structures.customerStructure.delayCategories);
  const [delayOptions, setDelayOptions] = useState<any>([]);

  // Calculate estimated flight duration based on user inputs.
  const flightDuration = useMemo(() => {
    if (!flight?.actualOut || !flight?.actualIn) {
      return "";
    }
    return convertMilli2DaysHrsMin(flight.actualIn.valueOf() - flight.actualOut.valueOf());
  }, [flight]);

  const etaOut: string = useMemo(() => {
    return !!flight && !!flight.actualOut ? moment(flight.actualOut).utc().format("HH:mm") : null;
  }, [flight]);

  const etaOff: string = useMemo(() => {
    return !!flight && !!flight.actualOff ? moment(flight.actualOff).utc().format("HH:mm") : null;
  }, [flight]);

  const etaOn: string = useMemo(() => {
    return !!flight && !!flight.actualOn ? moment(flight.actualOn).utc().format("HH:mm") : null;
  }, [flight]);

  const etaIn: string = useMemo(() => {
    return !!flight && !!flight.actualIn ? moment(flight.actualIn).utc().format("HH:mm") : null;
  }, [flight]);

  useEffect(() => {
    if (delayCategories) {
      const tempDelayOptions: IDelayOptions[] = [];
      delayCategories.forEach((cat: IDelayCategory) => {
        cat.delayCodes.forEach((opt: IDelayCode) => {
          const code = cat.title.trim().concat(opt.delayNumber.toString());
          tempDelayOptions.push({
            _id: opt._id, //this is what is passed to the backend
            _old_id: cat._old_id,
            title: cat.title,
            description: cat.description,
            delayCode: opt,
          });
        });
      });
      setDelayOptions(sortDelayOptions(tempDelayOptions, delayCodeFormat, delayCodeSortOrder));
    }
  }, [delayCategories, delayCodeFormat]);

  const outOfScope = useMemo(() => {
    if (!flight) {
      return true;
    }
    const now = new Date()?.valueOf();
    const sixHours = 21600000;
    return flight.actualOut?.valueOf() - now > sixHours || now - flight.actualIn?.valueOf() > sixHours;
  }, [flight]);

  // Setting openTabContent true after set false preserves the transition when display none is used.
  const selectTab = (value: any): void => {
    if (value === currentTabIndex) {
      return;
    }
    setCurrentTabIndex(value);
  };

  const renderDepartureForm = (): JSX.Element => {
    if (!flight) {
      return null;
    }
    const subtitle = flight.aircraft ? `(Assigned Aircraft: ${flight.aircraft.registration})` : "";
    return (
      <CardContent>
        {renderFormHeader("Submit Departure Message", etaOut, etaOff, etaOn, etaIn, flightDuration, flight, subtitle)}
        <DepartureMessages
          flightId={flight._id}
          customFields={flight.custom}
          delayOptions={delayOptions}
          origin={flight ? flight.origin : null}
          windowId={props.id}
          // Current Times
          actualOut={flight.actualOut?.valueOf()}
          actualOff={flight.actualOff?.valueOf()}
          actualOn={flight.actualOn?.valueOf()}
          actualIn={flight.actualIn?.valueOf()}
          setRefresh={setRefresh}
        />
      </CardContent>
    );
  };

  const renderArrivalForm = (): JSX.Element => {
    const subtitle = !!flight && flight.aircraft ? `(Assigned Aircraft: ${flight.aircraft.registration})` : "";
    return (
      <CardContent>
        {renderFormHeader("Submit Arrival Message", etaOut, etaOff, etaOn, etaIn, flightDuration, flight, subtitle)}
        <ArrivalMessages
          flightId={flight._id}
          customFields={flight.custom}
          delayOptions={delayOptions}
          destination={flight?.destination}
          windowId={props.id}
          actualOut={flight.actualOut?.valueOf()}
          actualOff={flight.actualOff?.valueOf()}
          actualOn={flight.actualOn?.valueOf()}
          actualIn={flight.actualIn?.valueOf()}
          setRefresh={setRefresh}
        />
      </CardContent>
    );
  };

  const renderFlightCommentsForm = (): JSX.Element => {
    const subtitle = !!flight && flight.aircraft ? `(Assigned Aircraft: ${flight.aircraft.registration})` : "";
    return (
      <CardContent>
        {renderFormHeader("Submit Flight Comment", etaOut, etaOff, etaOn, etaIn, flightDuration, flight, subtitle)}
        <FlightMessages flightId={flight._id} windowId={props.id} setRefresh={setRefresh} />
      </CardContent>
    );
  };

  return (
    <div ref={containerRef} style={{ display: "flex", width: "100%", height: "100%" }}>
      <Grid
        container
        sx={(theme) => movementMessageStyles(theme).MovementMessage_Container}
        style={{ flexDirection: "row" }}
      >
        <Grid item xs={"auto"} sx={(theme) => movementMessageStyles(theme).MovementMessage_Menu}>
          <div className="flightchip_group_container">{flight && <FlightChip data={flight} />} </div>
          <OcTabs
            variant="scrollable"
            orientation={"vertical"}
            scrollButtons="auto"
            value={currentTabIndex}
            onChange={(event: any, value: any) => selectTab(value)}
            sx={{ position: "relative", left: "6px" }}
          >
            <Tab wrapped={true} label="Departure" classes={{ root: "form-tab" }} />
            <Tab wrapped={true} label="Arrival" classes={{ root: "form-tab" }} />
            <Tab wrapped={true} label="Flight Comments" classes={{ root: "form-tab" }} />
          </OcTabs>
        </Grid>

        {flight && !outOfScope ? (
          <Grid item xs>
            <Card classes={"vertical"} sx={(theme) => movementMessageStyles(theme).MovementMessage_Card}>
              {currentTabIndex === 0 && renderDepartureForm()}
              {currentTabIndex === 1 && renderArrivalForm()}
              {currentTabIndex === 2 && renderFlightCommentsForm()}
              <CardContent sx={(theme) => movementMessageStyles(theme).flightId}>Flight ID: {flight._id}</CardContent>
            </Card>
          </Grid>
        ) : flight && outOfScope ? (
          <Grid item xs>
            <Card classes={"vertical"} sx={(theme) => movementMessageStyles(theme).MovementMessage_Card}>
              <CardContent>
                <Typography className="no_events" variant="body2">
                  Cannot apply movement messages to a flight that is more than 6 hours from the present time.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          <Grid item xs>
            <Card classes={"vertical"} sx={(theme) => movementMessageStyles(theme).MovementMessage_Card}>
              <CardContent>
                <Typography className="no_events" variant="body2">
                  Please select a valid flight event to submit movement messages.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </div>
  );
};
